import {FeatureModel} from "./integration/representation/viewmodel";
import datafangstHelpers from "./utilities";
import { noLocationals } from "./locationalUtilities";
import {Locational} from "./integration/representation/feature";
import {Direction, SidePosition, SidePositionEdit} from "./presentation/routes/contract-features-3/domain";
import {MeteringDirectionMissingError} from "./meteringDirectionUtilities";


export function getSidePositionForFeatureRelativeToMeteringDirection(feature: FeatureModel): SidePosition {
    if (allSidePositionsEqualRelativeToMeteringDirection(feature)) {
        return getSidePositionRelativeToMeteringDirection(feature.locational?.locationals[0]);
    } else {
        throw "Kan ikke oppgi sideposisjon for vegobjekt når ikke alle sideposisjoner er like.";
    }
}

export function allSidePositionsEqualRelativeToMeteringDirection(feature: FeatureModel): boolean {
    if (noLocationals(feature))
    {
        return true;
    }
    const sidePositionsRelativeToMeteringDirection = feature.locational.locationals
        .map(locational => getSidePositionRelativeToMeteringDirection(locational));
    return sidePositionsRelativeToMeteringDirection.every(
        sidePosition => sidePosition === sidePositionsRelativeToMeteringDirection[0]
    );
}

export function getAllSidePositionsAsTextRelativeToMeteringDirection(feature: FeatureModel): string {
    if (noLocationals(feature)) {
        return "";
    }
    return feature.locational.locationals
        .map(locational => getSidePositionRelativeToMeteringDirection(locational))
        .map(sidePosition => sidePositionFromDomain(sidePosition))
        .filter(sidePosition => sidePosition)
        .join(", ");
}

export function getSidePositionRelativeToMeteringDirection(locational: Locational): SidePosition {
    if (!locational) {
        return;
    }
    const sidePosition = locational.sidePosition;
    const meteringDirection = locational.meteringDirection;
    return reverseSidePositionIfMeteringDirectionAgainst(sidePosition, meteringDirection);
}

export function reverseSidePositionIfMeteringDirectionAgainst(sidePosition: SidePosition, meteringDirection: Direction): SidePosition {
    if (meteringDirection === "MOT") {
        return reverseSidePosition(sidePosition);
    }
    if (meteringDirection === "MED") {
        return sidePosition;
    }
    if (!isMeteringDirectionRelevant(sidePosition)) {
        return sidePosition;
    }
    // The side position is dependent on the metering direction, but the metering direction is unknown
    // We can't know the side position
    throw new MeteringDirectionMissingError("Kan ikke oppi sideposisjon når metreringsretning mangler");
}

function reverseSidePosition(sidePosition: SidePosition): SidePosition {
    switch(sidePosition) {
        case "LEFT":
            return "RIGHT";
        case "RIGHT":
            return "LEFT";
        case "MIDDLE_LEFT":
            return "MIDDLE_RIGHT";
        case "MIDDLE_RIGHT":
            return "MIDDLE_LEFT";
        case "LEFT_ACCESS":
            return "RIGHT_ACCESS";
        case "RIGHT_ACCESS":
            return "LEFT_ACCESS";
        default:
            return sidePosition;
    }
}

function isMeteringDirectionRelevant(sidePosition: SidePosition): boolean {
    switch(sidePosition) {
        case "LEFT":
        case "RIGHT":
        case "MIDDLE_LEFT":
        case "MIDDLE_RIGHT":
        case "LEFT_ACCESS":
        case "RIGHT_ACCESS":
            return true;
        default:
            return false;
    }
}

export function createSidePositionEdit(locational: Locational, newSidePositionRelativeToMeteringDirection: SidePosition, feature: FeatureModel): SidePositionEdit {
    const meteringDirection = locational.meteringDirection;
    const newSidePositionRelativeToGeometry = reverseSidePositionIfMeteringDirectionAgainst(
        newSidePositionRelativeToMeteringDirection, meteringDirection
    );
    return new SidePositionEdit(newSidePositionRelativeToGeometry, feature.id, locational.linkId);
}

export function sidePositionNorwegianToDomain(sidePosition: string): SidePosition {
    switch(sidePosition) {
        case "V":
            return "LEFT";
        case "H":
            return "RIGHT";
        case "HV":
            return "LEFT_AND_RIGHT";
        case "M":
            return "MIDDLE";
        case "X":
        case "K":
            return "CROSSING";
        case "MV":
            return "MIDDLE_LEFT";
        case "MH":
            return "MIDDLE_RIGHT";
        case "VT":
            return "LEFT_ACCESS";
        case "HT":
            return "RIGHT_ACCESS";
        case "R0":
            return "ROUNDABOUT_CENTRE";
        case "L":
            return "LONGITUDINAL";
        default:
            return null;
    }
}

export function sidePositionFromDomain(sidePosition: string) {
    switch(sidePosition) {
        case "LEFT":
            return "V";
        case "RIGHT":
            return "H";
        case "LEFT_AND_RIGHT":
            return "HV";
        case "MIDDLE":
            return "M";
        case "CROSSING":
            return "K";
        case "MIDDLE_LEFT":
            return "MV";
        case "MIDDLE_RIGHT":
            return "MH";
        case "LEFT_ACCESS":
            return "VT";
        case "RIGHT_ACCESS":
            return "HT";
        case "ROUNDABOUT_CENTRE":
            return "R0";
        case "LONGITUDINAL":
            return "L";
        default:
            return sidePosition;
    }
}

export function getSidePositionsNorwegian() {
    return ['V', 'H', 'HV', 'M', 'K', 'MV', 'MH', 'VT', 'HT', 'R0', 'L'];
}
